import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { SectionItemDto } from '../../../core/models/cms.model';
import { GridSlotPreset } from '../../../core/constants/grid-layout-presets';
import { getProductFeatureSlots, PRODUCT_FEATURE_BANNER_NATIVE, PRODUCT_FEATURE_GRID_VARIANTS } from '../../../core/constants/grid-layout-presets';
import { mediaProfileForSlotHint, sizesForSlotHint } from '../../../core/utils/media-image-profile.util';
import { MediaSrcDirective } from '../../directives/media-src.directive';

@Component({
  selector: 'app-product-feature-grid',
  standalone: true,
  imports: [CommonModule, RouterModule, MediaSrcDirective],
  templateUrl: './product-feature-grid.component.html',
  styleUrls: ['./product-feature-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductFeatureGridComponent {
  @Input({ required: true }) items: SectionItemDto[] = [];
  @Input() layoutVariant = 4;
  @Input() zoomOnClick = false;
  @Output() cellActivate = new EventEmitter<{ mediaUrl: string; mediaType: 'image' | 'video'; title: string }>();

  readonly bannerNative = PRODUCT_FEATURE_BANNER_NATIVE;

  get slots() {
    return getProductFeatureSlots(this.layoutVariant);
  }

  get gridClass(): string {
    if (this.layoutVariant === PRODUCT_FEATURE_GRID_VARIANTS.TWO) {
      return 'product-feature-grid--2';
    }
    if (this.layoutVariant === PRODUCT_FEATURE_GRID_VARIANTS.THREE) {
      return 'product-feature-grid--3';
    }
    return 'product-feature-grid--4';
  }

  mediaProfileForSlot(slot: GridSlotPreset) {
    return mediaProfileForSlotHint(slot.hintPx);
  }

  sizesForSlot(slot: GridSlotPreset): string {
    return sizesForSlotHint(slot.hintPx);
  }

  cellItem(sortOrder: number): SectionItemDto | undefined {
    return this.items.find((i) => i.sortOrder === sortOrder);
  }

  cellLink(item: SectionItemDto | undefined): string | null {
    if (this.zoomOnClick) {
      return null;
    }
    const url = item?.linkUrl?.trim();
    return url || null;
  }

  onCellActivate(item: SectionItemDto): void {
    if (!this.zoomOnClick || !item.mediaUrl?.trim()) {
      return;
    }
    this.cellActivate.emit({
      mediaUrl: item.mediaUrl,
      mediaType: item.mediaType === 'video' ? 'video' : 'image',
      title: item.title || '',
    });
  }

  onCellKeydown(event: KeyboardEvent, item: SectionItemDto): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onCellActivate(item);
    }
  }

  trackBySortOrder(_: number, slot: ReturnType<typeof getProductFeatureSlots>[number]): number {
    return slot.sortOrder;
  }
}
